// Blog post data for The Glass Spec
// Each post is stored as an object with markdown content

export const blogPosts = [
  {
    slug: 'shower-glass-pricing-guide',
    title: 'Shower Glass Pricing Guide: What to Expect for Your Bathroom Renovation',
    excerpt: 'A comprehensive breakdown of shower glass costs by frame type and configuration—from basic framed enclosures to luxury steam showers. Learn what factors affect pricing and how to budget for your project.',
    author: 'Comal Glass Team',
    date: '2026-06-26',
    category: 'Shower Glass',
    readTime: '7 min read',
    featured: true,
    image: '/images/local-projects/shower-boerne.jpg',
    content: `## Understanding Shower Glass Pricing

One of the most common questions we receive at Comal Glass is: "How much does a shower enclosure cost?" The honest answer is that pricing varies significantly based on your choices—but we believe in transparency. This guide breaks down what you can expect to pay for different types of shower glass installations in the Texas Hill Country.

**Note:** All prices below are estimates for installed shower enclosures, including professional measurement, fabrication, and installation. Actual pricing depends on your specific bathroom dimensions, glass thickness, hardware selection, and site conditions.

---

## Pricing by Frame Type

### Frameless Shower Enclosures
**Price Range: $1,200 – $4,500+**

Frameless enclosures represent the premium tier of shower glass. They use thicker glass (typically 3/8" or 1/2") that doesn't require metal framing for structural support, creating a clean, modern aesthetic that showcases your tile work.

**What affects frameless pricing:**
- **Glass thickness**: 3/8" glass is standard; 1/2" glass adds $200–$600
- **Glass type**: Clear glass is standard; low-iron Starphire® adds 25–35%
- **Hardware finish**: Chrome is most affordable; brushed gold/satin brass etc typically adds $150–$300
- **Protective coatings**: ShowerGuard or EnduroShield adds $200–$400

**Best for:** Modern bathrooms, luxury renovations, showcasing decorative tile

### Semi-Frameless Shower Enclosures
**Price Range: $1200 – $2,500**

Semi-frameless enclosures offer a middle ground—they have minimal framing (usually around the perimeter of fixed panels) while the door itself is frameless. This reduces cost while maintaining a relatively clean look.

**What affects semi-frameless pricing:**
- **Configuration complexity**: Simple doors cost less than corner enclosures
- **Glass thickness**: Typically 1/4" to 3/8"
- **Frame finish**: Standard finishes (chrome, brushed nickel) vs. specialty finishes

**Best for:** Balancing aesthetics and budget, traditional-to-transitional bathrooms

---

## Pricing by Configuration

### Inline Door and Panel
**Price Range: $1,400 – $2,500 (frameless)**

The most common configuration: a fixed panel with a hinged door, installed in a straight line along one wall. This works for alcove showers and tub-to-shower conversions.

**Typical specs:**
- One fixed panel + one door
- Usually 48"–60" total width
- Straightforward installation

### Sliding (Bypass) Doors
**Price Range: $1,600 – $2,200**

Sliding doors are ideal when swing clearance is limited. Two or more panels slide past each other on a track. Modern sliding systems can be frameless or semi-frameless with minimal hardware.

**Considerations:**
- Requires a track at the top and bottom (or top-hung systems for cleaner look)
- Maximum opening is half the total width
- Good for tub/shower combos

### 90-Degree (Corner) Enclosures
**Price Range: $1,600 – $3,000+ (frameless)**

Corner enclosures wrap around two walls, meeting at a 90-degree angle. They require precise measurement and additional hardware (clips, hinges, and a corner support).

**What adds cost:**
- Two glass panels meeting at a corner
- More hardware and installation complexity
- Larger overall glass area

**Best for:** Corner showers, maximizing space in smaller bathrooms

### Neo-Angle Enclosures
**Price Range: $1,800 – $3,500+ (frameless)**

Neo-angle enclosures fit into a corner but have three glass panels forming a "cut corner" entry—typically at 135-degree angles. They're ideal for smaller bathrooms where a full corner enclosure won't fit.

**What adds cost:**
- Custom angles require precise fabrication
- More seams and hardware
- Often 4 panels of glass total

**Best for:** Compact bathrooms, unique architectural situations

### Steam Shower Enclosures
**Price Range: $2,500 – $8,000+**

Steam showers are fully enclosed to contain steam and heat. They require a ceiling panel, transom above the door, and special sealing considerations. The door typically has a small gap at the bottom for ventilation (steam rises).

**What adds cost:**
- Full ceiling enclosure
- Transom panels
- Thicker glass
- More complex installation
- Additional waterproofing considerations

**Best for:** Spa-like primary bathrooms, wellness-focused homeowners

---

## Additional Factors That Affect Pricing

### Glass Upgrades
| Upgrade | Additional Cost |
|---------|----------------|
| Low-iron (Starphire®) glass | +20–25% |
| ShowerGuard permanent coating | +$200–$400 |
| Frosted or patterned glass | +50-75% |

### Hardware Upgrades
| Finish | Price Tier |
|--------|-----------|
| Chrome, Brushed Nickel | Standard |
| Oil-Rubbed Bronze | +$100–$200 |
| Matte Black | +$100–$200 |
| Satin Brass, Brushed Gold | +$150–$300 |
| Polished Gold | +$200–$400 |

### Site Conditions
- **Out-of-plumb walls**: Requires custom cutting (+$100–$300)
- **Tile installation timing**: We can quote before tile, but final templating must wait until tile and mounting surfaces are complete.
- **Removal of existing enclosure**: +$400–$600

---

## How to Budget for Your Project

Based on our experience in the Texas Hill Country, here's what most homeowners spend:

| Project Type | Typical Budget |
|-------------|----------------|
| Basic frameless inline (door + panel) | $1,500–$2,200 |
| Mid-range frameless corner enclosure | $2,500–$3,500 |
| Luxury frameless with upgrades | $2,500–$5,000 |
| Steam shower enclosure | $3,000–$6,000+ |

---

## Why Prices Vary Between Companies

You might get quotes ranging from $800 to $3,000+ for seemingly similar projects. Here's what often explains the difference:

1. **Glass thickness**: Some companies default to 1/4" glass; we use 3/8" or 1/2" for frameless
2. **Hardware quality**: Budget hardware wears out faster and shows corrosion, incorrect or reduced hardware can cause premature system failure
3. **Installation expertise**: Improper installation leads to leaks and premature failure
4. **Warranty coverage**: Ask what's covered and for how long
5. **Measurement precision**: Custom glass requires exact measurements—errors are expensive

---

## Get a Custom Quote

Every bathroom is different, and we believe in providing accurate quotes based on your specific space—not ballpark estimates over the phone.

**[Request a free in-home consultation](/contact)** and we'll:
- Measure your space precisely
- Discuss your design preferences
- Explain all your options
- Provide a detailed written quote

Or call us at **(830) 359-9925** to schedule your appointment.

---

*Serving Bulverde, Boerne, Fredericksburg, New Braunfels, Dripping Springs, and the entire Texas Hill Country with precision shower glass installation.*
`
  },
  {
    slug: 'serving-texas-hill-country-service-areas',
    title: 'Now Serving the Texas Hill Country: Your Local Glass Experts',
    excerpt: 'Comal Glass Company expands service throughout the Texas Hill Country, from Fredericksburg wine country to the Highland Lakes. Discover our custom glass solutions for your community.',
    author: 'Comal Glass Team',
    date: '2026-06-25',
    category: 'Company News',
    readTime: '5 min read',
    featured: false,
    image: '/images/blog/service-areas-hero.jpg',
    content: `## Your Neighborhood Glass Experts

At Comal Glass Company, we've always believed that great craftsmanship starts with understanding your community. That's why we're proud to announce expanded service throughout the Texas Hill Country—bringing our precision glass installations directly to homeowners in some of the most beautiful communities in Texas.

## Premium Shower Glass Across the Hill Country

Whether you're building a modern farmhouse in [Dripping Springs](/shower-glass-dripping-springs) or renovating a lakefront home in [Canyon Lake](/shower-glass-canyon-lake), our frameless shower enclosures are designed to maximize your views while meeting the highest standards of craftsmanship.

### Featured Communities for Shower Glass

- **[Boerne](/shower-glass-boerne)** – From Cordillera Ranch estates to Fair Oaks Ranch homes, we deliver precision-fitted frameless enclosures with hardware options to match any aesthetic
- **[Fredericksburg](/shower-glass-fredericksburg)** – Wine country luxury demands exceptional craftsmanship, and our matte black and brushed gold hardware options complement the area's distinctive style
- **[Johnson City](/shower-glass-johnson-city)** – At the heart of the Texas Wine Trail, we serve weekend retreats and permanent residences alike
- **[Blanco](/shower-glass-blanco)** – The Lavender Capital deserves glass work that honors its rural elegance
- **[New Braunfels](/shower-glass-new-braunfels)** – Bold design choices welcome here—from emerald green tile to brushed gold hardware

## Wine Cellar Glass for Texas Wine Country

With the Texas Wine Trail running through our service area, it's no surprise that wine storage is a popular request. Our thermally-broken aluminum framing and dual-pane insulated glass maintain the optimal 55°F cellar temperature while showcasing your collection.

### Wine Enclosure Service Areas

- **[Fredericksburg Wine Enclosures](/wine-enclosures-fredericksburg)** – The heart of Texas wine country, where proper storage meets beautiful display
- **[Marble Falls Wine Enclosures](/wine-enclosures-marble-falls)** – Highland Lakes homes with spectacular collections deserve spectacular glass
- **[Canyon Lake Wine Enclosures](/wine-enclosures-canyon-lake)** – Floor-to-ceiling frameless panels that make your collection the focal point
- **[Bulverde Wine Enclosures](/wine-enclosures-bulverde)** – Custom wine rooms that feel like walking into a jewel box

## Custom Office Partitions for the Remote Work Era

The shift to remote work has created demand for dedicated home office spaces that don't sacrifice natural light. Our industrial-style glass partitions—featuring black grid framing and sliding door options—carve out private workspaces while maintaining your home's open feel.

**Featured:** [Custom Office Glass in Canyon Lake](/office-partitions-canyon-lake) – See how we helped a homeowner create a stunning home office with divided-lite sliding glass doors.

## Why Local Matters

When you choose Comal Glass, you're not just getting a glass company—you're getting neighbors who understand your community:

- **Boerne & Kendall County** – We know the HOA requirements at Cordillera Ranch and Fair Oaks Ranch
- **Fredericksburg & Gillespie County** – We appreciate the blend of German heritage and modern Hill Country aesthetics
- **Dripping Springs & Hays County** – We understand the modern farmhouse style that defines the area
- **Marble Falls & the Highland Lakes** – We use marine-grade hardware for lakefront installations

## Ready to Get Started?

Whether you're building new or renovating, we offer free in-home consultations throughout the Texas Hill Country. Our team will measure your space, discuss your vision, and provide a detailed quote—typically with installation in as little as one week.

**[Contact us today](/contact)** or call **(830) 359-9925** to schedule your consultation.

---

*Serving Fredericksburg, Boerne, Marble Falls, Dripping Springs, Johnson City, Blanco, Canyon Lake, New Braunfels, Bulverde, Spring Branch, Wimberley, and surrounding Hill Country communities.*
`
  },
  {
    slug: 'irc-safety-glazing-laws-residential',
    title: 'Deciphering IRC Safety Glazing Laws for Luxury Residential Builds',
    excerpt: 'A definitive field guide to understanding IRC Section R308 and knowing exactly when tempered glass is legally mandatory in residential construction.',
    author: 'Comal Glass Team',
    date: '2025-06-23',
    category: 'Glass Types',
    readTime: '8 min read',
    featured: false,
    image: '/images/services/shower-glass-hero.jpeg',
    content: `## Safety Glazing in Modern Architecture

In custom home building and high-end remodeling, glass is no longer just a functional window to the outside—it is a structural and aesthetic statement. From soaring, floor-to-ceiling panoramic views to minimalist glass handrails and expansive shower enclosures, architectural glazing defines modern luxury.

However, with bold architectural choices comes strict structural accountability. The International Residential Code (IRC) enforces rigid mandates on where safety glazing—most commonly tempered glass—must be deployed.

Failing to design with these rules in mind doesn't just mean a failed inspection at closing; it introduces liability and safety compromises. Here is a definitive field guide to understanding IRC Section R308 (Glazing) and knowing exactly when tempered glass is legally mandatory in residential engineering.

## What Makes Tempered Glass a "Safety Glazing"?

Standard float glass breaks into large, razor-sharp shards that pose severe injury risks. Tempered glass undergoes a specialized thermal process where it is heated to approximately 1,200°F (649°C) and then rapidly cooled via high-pressure air blasts (quenching).

This process puts the outer surfaces into high compression and the interior into tension. Because of this stored energy, when tempered glass fails, it instantaneously fractures into thousands of small, blunt, relatively harmless cubes (often called "dicing").

### The Identification Rule

Per IRC R308.1, every single piece of safety glass must feature a permanent, legally compliant manufacturer's mark. For tempered glass, this logo must be permanently etched, fired, or sandblasted into the corner of the pane. **If it is a removable sticker, it will not pass inspection.**

## The 5 Major "Hazardous Locations" Requiring Tempered Glass

The IRC identifies specific zones in a residential layout where human impact is highly probable. If your architectural drawings place glass in these zones, it must be specified as tempered.

### 1. The Wet Zone: Showers, Tubs, and Pools (IRC R308.4.5)

Slip-and-fall hazards make bathrooms the most heavily regulated glazing environments.

**The Rule:** Any glazing in walls, enclosures, or barriers facing a shower, bathtub, hot tub, sauna, or pool requires tempering if the bottom exposed edge of the glass is less than 60 inches vertically above the standing or walking surface.

**The Exception:** If the glass panel is more than 60 inches away (measured horizontally in a straight line) from the water's edge, standard glass may be permitted.

### 2. Doors and Door Proximity (IRC R308.4.1 & R308.4.2)

Glass in motion or adjacent to foot traffic is a primary hazard zone.

**In Doors:** All glass panels within fixed, sliding, swinging, or bi-fold doors must be tempered, regardless of size.

**Adjacent to Doors:** If a window or fixed glass panel sits within a 24-inch arc of either vertical edge of a door (in the closed position), and the bottom edge of that glass is less than 60 inches from the floor, it must be tempered.

### 3. The "Low Window" Rule: Large Fixed or Operable Panels (IRC R308.4.3)

Large windows placed low to the ground can easily be mistaken for open walkways or struck by low-lying objects. A window must meet **all four** of the following criteria simultaneously to legally require tempering:

1. The exposed area of the individual pane is greater than 9 square feet
2. The bottom edge of the glass is less than 18 inches above the finished floor
3. The top edge of the glass is more than 36 inches above the finished floor
4. There is a walking surface within 36 inches horizontally of the window pane

### 4. Stairways, Ramps, and Landings (IRC R308.4.6 & R308.4.7)

The kinetic energy of someone falling down a staircase requires robust structural protection.

**Along the Run:** Glazing adjacent to stairways, ramps, and intermediate landings must be tempered if the bottom edge of the glass is less than 36 inches above the plane of the walking surface.

**At the Bottom Landing:** Glazing within a 60-inch horizontal arc of the bottom tread of a stairway must be safety glass if its bottom edge is less than 36 inches above the landing surface.

### 5. Glass Railings and Guards (IRC R308.4.4)

Whether structural balusters or non-structural in-fill panels, all glass used in railings, balconies, or protective guards must be safety glass.

**Specification Nuance:** While monolithic tempered glass is common, luxury architectural standards frequently upgrade guardrails to laminated tempered glass (two layers of tempered glass fused with an interlayer like PVB or SGP). This ensures that if one layer shatters, the interlayer holds the structure rigid, preventing a dangerous fall.

## Architectural Summary Reference Table

| Location | Critical Dimension / Trigger | Mandatory Requirement |
|----------|------------------------------|----------------------|
| Shower Enclosures | Bottom edge less than 60" from drain/floor | Tempered Mandatory |
| Sidelights / Adjacent to Doors | Within 24" of door edge & bottom edge less than 60" high | Tempered Mandatory |
| Floor-to-Ceiling Windows | Area greater than 9 sq ft & bottom edge less than 18" from floor | Tempered Mandatory |
| Staircase Handrails | All glass guard and railing systems | Tempered / Laminated Mandatory |

## The Bottom Line

By understanding these parameters during the drafting and framing stages, custom builders can eliminate late-stage scheduling delays, minimize change-order friction, and ensure the structural integrity of the project matches its luxury aesthetic.

At Comal Glass Company, we work directly with architects, builders, and homeowners to ensure every glass specification meets IRC requirements while achieving the design vision. Contact us early in your project for a code-compliant glazing consultation.`
  },
  {
    slug: 'frameless-vs-semi-frameless-shower-glass',
    title: 'Frameless vs. Semi-Frameless Shower Glass: Which Is Right for Your Bathroom?',
    excerpt: 'Understanding the key differences between frameless and semi-frameless shower enclosures to help you make the best choice for your space, budget, and style.',
    author: 'Comal Glass Team',
    date: '2025-06-20',
    category: 'Shower Glass',
    readTime: '5 min read',
    featured: true,
    image: '/images/portfolio/Image-1-2.jpeg',
    content: `## The Great Debate: Frameless vs. Semi-Frameless

When it comes to custom shower enclosures, one of the first decisions you'll face is choosing between frameless and semi-frameless designs. Both options offer distinct advantages, and the right choice depends on your bathroom layout, budget, and aesthetic preferences.

### What is Frameless Shower Glass?

Frameless shower enclosures use **thicker tempered glass** (typically 3/8" or 1/2") that's strong enough to stand on its own without metal framing around the perimeter. The glass panels are held in place by minimal hardware—hinges, clamps, and handles—creating a sleek, modern look.

**Key characteristics:**

- 3/8" or 1/2" thick tempered safety glass
- No metal framing around glass edges
- Heavy-duty hinges and clamps (CR Laurence, FHC)
- Clean, contemporary aesthetic
- Maximum transparency and light flow

### What is Semi-Frameless Shower Glass?

Semi-frameless enclosures use **slightly thinner glass** (usually 1/4" to 3/8") with a metal frame around the door, while the fixed panels remain frameless. This hybrid approach offers a balance between the sleek frameless look and the structural support of framed designs.

**Key characteristics:**

- 1/4" to 3/8" tempered safety glass
- Frame around the door only
- Frameless fixed panels
- More affordable than fully frameless
- Still offers a modern appearance

### Comparing the Two Options

| Feature | Frameless | Semi-Frameless |
|---------|-----------|----------------|
| Glass Thickness | 3/8" - 1/2" | 1/4" - 3/8" |
| Hardware | Minimal, high-end | Moderate |
| Aesthetic | Ultra-modern, luxury | Modern, clean |
| Price Point | Higher | Mid-range |
| Maintenance | Easy to clean | Slightly more maintenance |
| Installation | Requires precision | More forgiving |

### When to Choose Frameless

Frameless shower enclosures are ideal when:

1. **You want a luxury, spa-like aesthetic** — Nothing beats the clean lines of frameless glass
2. **Your bathroom has beautiful tile work** — Frameless glass showcases your investment
3. **You prefer minimal cleaning** — No frame channels to trap soap scum
4. **You're building a high-end home** — Frameless adds significant value

### When to Choose Semi-Frameless

Semi-frameless might be the better choice when:

1. **You have a tighter budget** — Get a modern look at a lower price point
2. **Your opening isn't perfectly square** — The frame provides adjustment room
3. **You want durability with style** — The frame adds structural rigidity
4. **You're updating a rental or flip property** — Great value for the investment

### The Bottom Line

Both frameless and semi-frameless shower enclosures are excellent choices that will elevate your bathroom. At Comal Glass Company, we install both styles with the same attention to detail and precision craftsmanship.

**Ready to decide?** Schedule a free in-home consultation and we'll help you choose the perfect enclosure for your space.`
  },
  {
    slug: 'low-iron-starphire-glass-explained',
    title: 'Low-Iron Starphire Glass: Why Crystal-Clear Matters',
    excerpt: 'Discover why low-iron ultra-clear glass is the preferred choice for high-end shower enclosures and how it eliminates the green tint in standard glass.',
    author: 'Comal Glass Team',
    date: '2025-06-15',
    category: 'Glass Types',
    readTime: '4 min read',
    featured: true,
    image: '/images/services/shower-glass-hero.jpeg',
    content: `## What is Low-Iron Glass?

If you've ever looked at the edge of a piece of glass, you may have noticed a green tint. This is caused by iron oxide—a natural impurity present in standard float glass. While barely noticeable in thin pieces, this green tint becomes increasingly apparent in thicker glass like the 3/8" and 1/2" panels used in frameless shower enclosures.

**Low-iron glass**, often sold under the brand name **Starphire®** by Vitro (formerly PPG), has the iron content reduced during manufacturing, resulting in crystal-clear transparency.

### Standard Clear vs. Low-Iron: The Visual Difference

| Characteristic | Standard Clear | Low-Iron (Starphire) |
|---------------|----------------|----------------------|
| Iron Content | ~0.1% | ~0.01% |
| Light Transmission | 83-84% | 91% |
| Edge Color | Green tint | Crystal clear |
| Best For | General use | White bathrooms, showcasing tile |

### When Low-Iron Glass Makes the Biggest Impact

**1. White or Light-Colored Bathrooms**

In bathrooms with white subway tile, marble, or light-colored stone, the green tint of standard glass can clash with your color palette. Low-iron glass maintains the true whites and neutrals you carefully selected.

**2. Showcasing Premium Tile Work**

If you've invested in beautiful porcelain, natural stone, or custom tile work, low-iron glass ensures your tile colors appear true-to-life without color distortion.

**3. Thick Glass Applications**

The thicker the glass, the more noticeable the green tint. For 1/2" frameless enclosures, low-iron glass makes a significant visual difference.

**4. Matching Other Glass Elements**

If your bathroom features a frameless mirror (which is typically low-iron), using low-iron shower glass creates visual consistency.

### The Investment Perspective

Low-iron glass typically costs **15-25% more** than standard clear glass. For a typical shower enclosure, this might add $200-$500 to your project. Consider this a worthwhile investment if:

- Your bathroom design features whites and light neutrals
- You're building a high-end custom home
- Visual perfection matters to you
- You want to showcase expensive tile work

### Our Recommendation

At Comal Glass Company, we offer both standard clear and low-iron Starphire glass. During your consultation, we'll show you samples of both so you can see the difference in your actual bathroom lighting.

For most of our Hill Country clients building custom homes, low-iron glass has become the standard specification—and once you see the difference, you'll understand why.`
  },
  {
    slug: 'showerguard-coating-worth-it',
    title: 'Is ShowerGuard Coating Worth the Investment?',
    excerpt: 'An honest look at ShowerGuard protective coating—what it does, how it works, and whether the lifetime warranty justifies the added cost.',
    author: 'Comal Glass Team',
    date: '2025-06-10',
    category: 'Shower Glass',
    readTime: '6 min read',
    featured: false,
    image: '/images/portfolio/Image-2-2.jpeg',
    content: `## What is ShowerGuard?

ShowerGuard is a **permanent protective coating** applied to glass during the manufacturing process by Guardian Glass. Unlike aftermarket coatings that sit on the surface and wear off over time, ShowerGuard is fused into the glass using an ion-beam process, making it a permanent part of the glass itself.

### How ShowerGuard Works

The ion-beam process modifies the glass surface at a molecular level, creating a barrier that:

1. **Resists hard water stains** — Mineral deposits from Hill Country water don't bond as readily
2. **Repels soap scum** — Soap residue washes away more easily
3. **Prevents corrosion** — The glass surface won't degrade over time
4. **Maintains clarity** — Your glass stays clearer, longer

### ShowerGuard vs. Aftermarket Coatings

| Feature | ShowerGuard | Aftermarket Coatings |
|---------|-------------|---------------------|
| Application | Factory-applied, permanent | Applied after installation |
| Durability | Lifetime (won't wear off) | 1-5 years, reapplication needed |
| Coverage | Both surfaces of glass | Typically interior only |
| Warranty | Limited Lifetime | Varies, often limited |
| Maintenance | Minimal | Regular reapplication |

### The Hill Country Hard Water Factor

Here in the Texas Hill Country, we have notoriously hard water. High mineral content means:

- White calcium deposits build up quickly on standard glass
- Regular cleaning is required to maintain clarity
- Harsh cleaners can damage glass surfaces over time

ShowerGuard significantly reduces (but doesn't eliminate) these issues. Our clients report cleaning their ShowerGuard enclosures about half as often as standard glass.

### Cost-Benefit Analysis

ShowerGuard typically adds **$200-$400** to a shower enclosure project. Here's how to think about the investment:

**It's worth it if:**

- You have very hard water
- You dislike frequent cleaning
- You want a "set it and forget it" solution
- Long-term value matters more than upfront cost
- You're building your forever home

**It may not be necessary if:**

- You have a water softener system
- You don't mind regular squeegee maintenance
- Budget is a primary concern
- You're selling the home soon

### Our Professional Opinion

After installing hundreds of ShowerGuard enclosures in the Hill Country, we can confidently say: **most clients who choose ShowerGuard are glad they did**. The reduced maintenance and lasting clarity provide genuine value.

However, we also have plenty of satisfied clients with standard glass who maintain beautiful enclosures with regular care. A quick squeegee after each shower goes a long way.

### The Bottom Line

ShowerGuard is a legitimate product that delivers on its promises. Whether it's "worth it" depends on your priorities, water quality, and willingness to maintain your enclosure. We're happy to discuss your specific situation during your consultation.`
  },
  {
    slug: 'glass-railing-mounting-systems-guide',
    title: 'Glass Railing Mounting Systems: A Complete Guide',
    excerpt: 'Base shoe, post-mount, standoff, or spigot? Learn the pros and cons of each glass railing mounting system to choose the right one for your project.',
    author: 'Comal Glass Team',
    date: '2025-06-05',
    category: 'Glass Railings',
    readTime: '7 min read',
    featured: false,
    image: '/images/portfolio/glass-railing-hillcountry-1.jpeg',
    content: `## Choosing the Right Mounting System

The mounting system you choose for your glass railing affects not only the aesthetics but also the installation complexity, cost, and long-term maintenance. Here's our complete guide to the four main options.

### 1. Base Shoe (U-Channel) System

**How it works:** An aluminum channel is mounted to the floor or deck surface. The glass panel sits inside the channel and is secured with setting blocks and wet-glazed with silicone.

**Pros:**

- Clean, minimal appearance
- Glass appears to rise directly from the floor
- No visible posts or hardware on the glass face
- Moderate cost
- Relatively straightforward installation

**Cons:**

- Requires adequate substrate for anchoring
- Channel collects debris (needs periodic cleaning)
- Glass replacement requires re-glazing

**Best for:** Decks, balconies, modern homes, clean architectural lines

### 2. Post-Mount with Clamps

**How it works:** Round or square stainless steel posts are anchored to the surface. Glass panels are secured to the posts using adjustable clamps.

**Pros:**

- Classic, timeless appearance
- Easy glass panel replacement
- Visible hardware can be a design feature
- Forgiving on uneven surfaces
- Posts provide additional rigidity

**Cons:**

- Posts interrupt the glass plane
- More hardware visible
- Higher material cost for quality posts

**Best for:** Traditional homes, staircases, pool fences, areas needing easy glass replacement

### 3. Standoff Pin System

**How it works:** Cylindrical stainless steel standoffs pass through holes drilled in the glass, creating a floating effect with the glass set off from the mounting surface.

**Pros:**

- Distinctive modern aesthetic
- Glass "floats" away from the structure
- Excellent for interior applications
- Creates interesting shadow lines

**Cons:**

- Requires precise hole drilling in glass
- More expensive fabrication
- Holes in glass can be a concern for some clients

**Best for:** Interior staircases, modern commercial spaces, architectural statements

### 4. Spigot (Core-Mount) System

**How it works:** Glass panels mount into floor-anchored stainless steel spigots. The spigots are core-drilled into concrete or attached to structural plates.

**Pros:**

- Ultimate minimal appearance
- No visible channels or posts
- Very secure anchoring
- Premium, high-end look

**Cons:**

- Highest cost option
- Requires concrete or substantial structure
- Most complex installation
- Less forgiving on irregular surfaces

**Best for:** High-end residential, luxury pools, rooftop decks, infinity-edge effects

### Stainless Steel Grades

Regardless of mounting system, hardware grade matters:

- **304 Stainless Steel:** Standard grade, suitable for interior and protected exterior
- **316 Stainless Steel:** Marine-grade, required for pools, coastal areas, high humidity

### Making Your Decision

Consider these factors:

1. **Aesthetic preference** — Do you want minimal or visible hardware?
2. **Location** — Interior vs. exterior, pool area vs. deck
3. **Structure** — What can your substrate support?
4. **Budget** — Spigot costs more than base shoe
5. **Maintenance** — How easy should glass replacement be?

### Our Recommendation

For most Hill Country deck and balcony projects, we recommend the **base shoe system** for its clean appearance and moderate cost. For pool fencing where code requires specific heights and easy access, **post-mount systems** offer practical advantages.

Schedule a consultation and we'll assess your specific project to recommend the ideal mounting system.`
  },
  {
    slug: 'wine-cellar-glass-thermal-considerations',
    title: 'Glass Wine Cellars: Understanding Thermal Performance',
    excerpt: 'Why thermal breaks, insulated glass, and proper sealing are critical for maintaining optimal wine storage conditions behind a glass wall.',
    author: 'Comal Glass Team',
    date: '2025-05-28',
    category: 'Wine Enclosures',
    readTime: '5 min read',
    featured: false,
    image: '/images/services/wine_room.jpg',
    content: `## The Challenge of Glass Wine Cellars

Wine requires stable storage conditions: approximately **55°F temperature** and **60-70% relative humidity**. Creating these conditions behind a wall of glass presents unique engineering challenges that, when solved correctly, result in stunning display cellars that protect your investment.

### Why Thermal Performance Matters

Glass is inherently a poor insulator. Without proper engineering:

1. **Temperature fluctuations** stress wine and accelerate aging
2. **Condensation** forms on glass surfaces, potentially damaging labels
3. **Cooling systems** run constantly, increasing energy costs
4. **Humidity** escapes, drying corks and allowing oxidation

### The Three Pillars of Thermal Performance

#### 1. Insulated Glass Units (IGUs)

Single-pane glass is insufficient for wine cellars. We specify **dual-pane insulated glass units** featuring:

- Two panes of tempered glass
- Argon gas fill between panes (better insulator than air)
- Warm-edge spacer technology
- Low-E coating to reflect heat

For extreme temperature differentials (very warm rooms, passive cooling), **triple-pane IGUs** provide maximum insulation.

#### 2. Thermally-Broken Frames

The frame system is equally important. **Thermally-broken aluminum frames** feature:

- Polyamide barrier between interior and exterior aluminum
- Prevents temperature transfer through the frame
- Eliminates condensation on frame surfaces
- Maintains seal integrity

Standard aluminum frames without thermal breaks will sweat and potentially drip onto your wine collection.

#### 3. Proper Sealing

The door and fixed panel seals must maintain the climate barrier:

- **Magnetic gaskets** provide consistent seal pressure
- **Compression seals** at floor and ceiling
- **Weatherstripping** rated for temperature differentials
- Professional installation ensuring no gaps

### Cooling System Compatibility

Your glass enclosure must work with your cooling system. We coordinate with:

- **WhisperKOOL** — Industry standard for quiet operation
- **CellarPro** — High-performance units for demanding applications
- **Wine Guardian** — Commercial-grade reliability

The cooling system should be sized for your cellar volume PLUS the additional load from the glass wall.

### UV Protection

Wine is sensitive to light, particularly UV rays. Our wine cellar glass includes:

- **Low-E coating** blocking 99% of UV radiation
- Optional tinted glass for additional protection
- Consideration of ambient light sources

### Cost Considerations

A properly engineered glass wine cellar enclosure costs more than a standard glass wall. Budget for:

- Dual-pane IGUs (vs. single pane)
- Thermally-broken frame systems
- Quality seals and gaskets
- Professional installation with climate testing

This investment protects your wine collection—potentially worth far more than the enclosure itself.

### Our Process

When you contact us about a wine cellar project, we:

1. Assess your space and climate conditions
2. Coordinate with your cooling system provider
3. Specify appropriate glass and frame systems
4. Install with attention to thermal performance
5. Test seal integrity before handoff

Your wine deserves proper protection. Let's design an enclosure that's as functional as it is beautiful.`
  }
];

// Helper function to get all posts
export const getAllPosts = () => {
  return blogPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Helper function to get featured posts
export const getFeaturedPosts = () => {
  return blogPosts.filter(post => post.featured).sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Helper function to get post by slug
export const getPostBySlug = (slug) => {
  return blogPosts.find(post => post.slug === slug);
};

// Helper function to get posts by category
export const getPostsByCategory = (category) => {
  return blogPosts.filter(post => post.category === category).sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Get all unique categories
export const getCategories = () => {
  return [...new Set(blogPosts.map(post => post.category))];
};
